"use client";

import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css/pagination";
import "swiper/css";
import "swiper/css/navigation";
import styles from "./featuresSwiper.module.scss";
import TitleWithDescriptionV2 from "../titleWithDescriptionV2/titleWithDescriptionV2";
import { svgImages } from "@/lib/svgImages";
import CustomSpinner from "../spinner/spinner";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useRouter } from "next/router";

const FeaturesSwiper = () => {
  const [loading, setLoading] = useState(true);
  const [featuresData, setFeaturesData] = useState(null);
  const [allImages, setAllImages] = useState(null);
  const [isAutoplay, setIsAutoplay] = useState(false);
  const sectionRef = useRef(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const router = useRouter();

  useEffect(() => {
    (async () => {
      var productData = null;

      const data = await fetch(
        "https://cms.qrhub.io/wp-json/wp/v2/features_page/874"
      ).then((res) => res.json());

      setFeaturesData(data);
      for (let index = 0; index < data.length; index++) {
        const element = data[index];
        productData = element;
        const svgData = await fetch("/images.json").then((res) => res.json());
        setAllImages(svgData);
        break;
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.create({
      trigger: sectionRef.current,
      start: "top 80%",
      end: "bottom 20%",
      onEnter: () => {
        if (swiperRef.current?.autoplay) {
          swiperRef.current.autoplay.start();
          setIsAutoplay(true);
        }
      },
      onLeave: () => {
        if (swiperRef.current?.autoplay) {
          swiperRef.current.autoplay.stop();
          setIsAutoplay(false);
        }
      },
      onEnterBack: () => {
        if (swiperRef.current?.autoplay) {
          swiperRef.current.autoplay.start();
          setIsAutoplay(true);
        }
      },
      onLeaveBack: () => {
        if (swiperRef.current?.autoplay) {
          swiperRef.current.autoplay.stop();
          setIsAutoplay(false);
        }
      },
    });

    return () => {
      if (swiperRef.current?.autoplay) {
        swiperRef.current.autoplay.stop();
      }
    };
  }, [loading]);

  return (
    <>
      {loading ? (
        <>
          <CustomSpinner />
        </>
      ) : (
        <>
          <div className={styles.featuresSwiper} ref={sectionRef}>
            <div className={`custom-container`}>
              <div className={`inner`}>
                <div className={styles.featuresSwiperTitle}>
                  <TitleWithDescriptionV2
                    title="Why Choose <br/> QRHUB?"
                    description="No technical expertise required our step-by-step guide ensures anyone can generate and use QR codes effectively."
                    light={true}
                    refreshTrigger={router.asPath}
                  />
                </div>
                <div className={styles.sliderContainer}>
                  <Swiper
                    modules={[Navigation, Autoplay, Pagination]}
                    centeredSlides={false}
                    loop={false}
                    navigation={{
                      nextEl: `.${styles.swiperButtonNext}`,
                      prevEl: `.${styles.swiperButtonPrev}`,
                    }}
                    autoplay={{
                      delay: 3000,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                      stopOnLastSlide: false,
                    }}
                    onSwiper={(swiper) => {
                      swiperRef.current = swiper;
                    }}
                    pagination={{
                      el: `.${styles.swiperPagination}`,
                      clickable: true,
                    }}
                    breakpoints={{
                      0: {
                        slidesPerView: 1,
                        spaceBetween: 10,
                        autoplay: {
                          disableOnInteraction: true,
                          pauseOnMouseEnter: false,
                        },
                      },

                      480: {
                        slidesPerView: 2,
                        spaceBetween: 20,
                      },
                      1025: {
                        slidesPerView: 3,
                        spaceBetween: 30,
                      },
                    }}
                    className={styles.featuresSwiperElement}
                    initialSlide={0}
                    grabCursor={true}
                  >
                    {featuresData?.acf.features_sections &&
                      featuresData?.acf.features_sections.map((slide) => (
                        <SwiperSlide
                          key={slide.feature_title}
                          className={styles.swiperSlide}
                        >
                          <Link
                            href={`/en/features/`}
                            className={styles.cardLink}
                          >
                            <div className={styles.card}>
                              <div className={styles.cardTitleContainer}>
                                <h3
                                  className={`${styles.cardTitle} font-size-32 line-height-title`}
                                  dangerouslySetInnerHTML={{
                                    __html: slide.feature_title,
                                  }}
                                />
                                {slide.coming_soon && (
                                  <div className={`${styles["comingSoon"]}`}>
                                    <div
                                      className={`${styles["comingSoonText"]}`}
                                    >
                                      Soon
                                    </div>
                                  </div>
                                )}
                              </div>
                              <p
                                className={`${styles.cardDescription} font-size-18 line-height-content`}
                                dangerouslySetInnerHTML={{
                                  __html: slide.short_description,
                                }}
                              />
                              <div className={styles.cardFooter}>
                                Learn More
                                <span
                                  className={styles.arrow}
                                  dangerouslySetInnerHTML={{
                                    __html: svgImages.navRight,
                                  }}
                                />
                              </div>
                            </div>
                          </Link>
                        </SwiperSlide>
                      ))}
                  </Swiper>
                  <div className={`${styles.swiperPagination} d-none-md`}></div>
                  <div className={`${styles.navContainer}`}>
                    <div
                      className={`${styles.swiperButtonPrev} ${styles.navButton} d-none-xs d-flex-md`}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: svgImages.arrowLeft,
                        }}
                      />
                    </div>
                    <div
                      className={`${styles.swiperButtonNext} ${styles.navButton} d-none-xs d-flex-md`}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: svgImages.arrowRight,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FeaturesSwiper;
